import { ReactElement } from "react";

import type { TenantPreferences } from "../argo/tenantPreferences";
import type { SignedInUserCompany } from "../../hooks/useSignedInUserCompany";

export type CopilotContextSelection = Array<{
  id: string | number;
  name: string;
}>;

export type { TenantPreferences, SignedInUserCompany };

export interface CopilotContext {
  workspace_id: string;
  active_url: string;
  spend_category_filter: CopilotContextSelection;
  industry_filter: CopilotContextSelection;
  geography_filter: CopilotContextSelection;
  employee_range_filter: {
    min: number | undefined;
    max: number | undefined;
  };
  revenue_range_filter: {
    min: number | undefined;
    max: number | undefined;
  };
  competitor_products_filter: CopilotContextSelection;
  whitespace?: {
    file_id: string;
    file_name: string;
  };
  // Gated by LaunchDarkly flag `ma-project-segment-improvements`. Absent when the flag is off.
  workspace_name?: string;
  workspace_description?: string;
  workspace_owner_email?: string;
  user_email?: string;
  self_product_categories?: CopilotContextSelection;
  icp_products?: CopilotContextSelection;
  icp_customer_match_file?: {
    file_id: string;
    file_name: string;
  };
  // Gated by LaunchDarkly flag `maCopilotContextEnrichment`. Independent of
  // `maProjectSegmentImprovements` — either field can ship with the
  // legacy/improved blocks present or absent.
  tenant_preferences?: TenantPreferences;
  signed_in_user_company?: SignedInUserCompany;
}

export enum ChoiceType {
  IT_SPEND_FILTER = "it_spend_filter",
  INDUSTRY_FILTER = "industry_filter",
  GEOGRAPHY_FILTER = "geography_filter",
  EMPLOYEE_RANGE_FILTER = "employee_range_filter",
  REVENUE_RANGE_FILTER = "revenue_range_filter",
  COMPETITIVE_ANALYSIS = "competitive_analysis",
}

export interface Choice {
  id: string;
  name: string;
  reason: string;
  min?: number;
  max?: number;
}

export enum MessageType {
  AI_MESSAGE = "ai_message",
  HUMAN_MESSAGE = "human_message",
  AI_SELECT_MESSAGE = "ai_select_message",
  AI_CHUNK = "ai_chunk",
  TOOL_MESSAGE = "tool_message",
  UI_MESSAGE = "ui_message",
  LOADING = "loading",
}

export enum RateType {
  BOOLEAN = "boolean",
  PERCENTAGE = "percentage",
}

export interface Rate {
  id: string;
  rate_type: RateType;
  rate_value: number;
}

export interface Message {
  id: string;
  payload: {
    content: string;
    react_content?: ReactElement;
    timestamp: Date;
    choice_type?: string;
    choices?: Choice[];
  };
  type: MessageType;
  rate: Rate | null;
}

export interface Thread {
  id: string;
  messages: Message[];
}
